import { type CampoSemanticoProcedimento } from '@framework/api-client/api/backoffice/schemas/campoSemanticoProcedimento.ts';
import { type AnagraficaDTO } from '@framework/api-client/api/inquiries/schemas/anagraficaDTO.ts';
import { type IDFattispecie } from '@framework/api-client/api/inquiries-compile/schemas/iDFattispecie.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type ValoriSezione } from '@framework/api-client/api/inquiries-compile/schemas/valoriSezione.ts';
import { type TagSemanticoDTO } from '@framework/api-client/api/users/schemas/tagSemanticoDTO.ts';
import {
  useCurrentUser,
  useCurrentUserData,
} from '@framework/auth/state/AuthSlice.tsx';
import { deserializeContextSnapshots } from '@framework/common-web/engine/engine-validation/context-snapshot/deserializeContextSnapshot.ts';
import { type IEngineValidationContextParams } from '@framework/common-web/engine/engine-validation/IEngineValidationContext.ts';
import { backendToRawSemanticFields } from '@framework/common-web/engine/engine-validation/parseSemanticFields.ts';
import { useMemo } from 'react';
import { SubmissionLogic } from '../../submission/SubmissionLogic.ts';
import { type IDelegationContext } from '../delegation/context/DelegationContext.tsx';

interface IModuleParam {
  fattispecie?: IDFattispecie | null;
  fattispecieAttivante?: IDFattispecie | null;
}

interface IParams {
  submission: RichiestaDTO | null | undefined;
  delegationContext: IDelegationContext | null;
  registers: AnagraficaDTO[] | null | undefined;
  semanticTags?: TagSemanticoDTO[] | null;
  /**
   * Campi semantici della pratica ottenuti dall'endpoint dedicato
   * `GET /procedimenti/{idIstanzaProcedimento}/campi-semantici-pratica`
   */
  dossierSemanticFields: CampoSemanticoProcedimento[] | null | undefined;
  isReadonly: boolean;
  values: ValoriSezione[] | null | undefined;
  module: IModuleParam | null | undefined;
}

/**
 * Memoizza il context di validazione per evitare di reimpostare
 * gli `initialValues` in modo errato.
 */
export function useValidationContextParams(
  params: IParams,
): IEngineValidationContextParams {
  const {
    submission,
    delegationContext,
    registers,
    semanticTags,
    dossierSemanticFields,
    isReadonly,
    values,
    module,
  } = params;

  const user = useCurrentUser();
  const userData = useCurrentUserData();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Non aggiungiamo user e userData come dipendenze in quanto il loro refresh non deve influire sul context di validazione e soprattutto sugli `initialValues`, user?.utenteLoggato e userData
  return useMemo(() => {
    return {
      user: {
        ...user?.utenteLoggato,
        custom: userData,
      },
      semanticFields: backendToRawSemanticFields(submission?.campiSemantici),
      semanticTags: semanticTags ?? null,
      dossierSemanticFields: backendToRawSemanticFields(dossierSemanticFields),
      applicant: delegationContext?.mainApplicant?.data,
      attorney: delegationContext?.delegationData?.attorney?.data,
      registers,
      readonly: isReadonly,
      contextSnapshot: deserializeContextSnapshots(values),
      submission: {
        id: submission?.id,
        progressivoId: submission?.progressivoId,
        progressivo: submission?.progressivo,
      },
      submissionModule: {
        fattispecie: {
          codice: module?.fattispecie?.codice,
          versione: module?.fattispecie?.versione,
          codiceProcedimento: module?.fattispecie?.codiceProcedimento,
          versioneProcedimento: module?.fattispecie?.versioneProcedimento,
          faseSchematron: module?.fattispecie?.faseSchematron,
        },
        fattispecieAttivante: {
          codice: module?.fattispecieAttivante?.codice,
          versione: module?.fattispecieAttivante?.versione,
          codiceProcedimento: module?.fattispecieAttivante?.codiceProcedimento,
          versioneProcedimento:
            module?.fattispecieAttivante?.versioneProcedimento,
          faseSchematron: module?.fattispecieAttivante?.faseSchematron,
        },
      },
      isIntegration: SubmissionLogic.isIntegration(submission),
      // Non vengon utilizzati FE
      externalServiceContext: null,
      trustedEnvironment: false,
    } satisfies IEngineValidationContextParams;
  }, [
    submission,
    dossierSemanticFields,
    delegationContext?.mainApplicant?.data,
    delegationContext?.delegationData?.attorney?.data,
    registers,
    semanticTags,
    isReadonly,
    values,
  ]);
}
